import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { getRecipeImageSource } from '@/constants/recipePlaceholders';
import { useApp } from '@/context/AppContext';
import {
  useAddRecipeToGroceryList,
  useGroceryLists,
  useCreateList,
  useUpdateList,
  type GroceryItem,
} from '@/hooks/useGroceryLists';
import { useDeleteRecipe, useRecipe } from '@/hooks/useRecipes';
import { Ionicons } from '@expo/vector-icons';
import { triggerSuccess as hapticSuccess } from '@/lib/haptics';
import { trackEvent } from '@/lib/analytics';
import { LinearGradient } from 'expo-linear-gradient';
import { validateNewListName } from '@/lib/grocery-lists-page.logic';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useFocusEffect } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Image, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Input } from '@/components/ui/input';
import { Brand, Neutrals } from '@/constants/theme';

export default function RecipeDetail() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const recipeId = typeof id === 'string' ? id : Array.isArray(id) ? id[0] : undefined;
  const router = useRouter();
  const { recipe, loading, error, refetch } = useRecipe(recipeId);
  const { deleteRecipe } = useDeleteRecipe();

  // Re-sync from cache when returning from edit (cache is updated on save, but we need a re-render)
  useFocusEffect(
    useCallback(() => {
      if (recipeId) refetch();
    }, [recipeId, refetch])
  );
  const { settings } = useApp();
  const insets = useSafeAreaInsets();
  const { lists: groceryLists, refetch: refetchLists } = useGroceryLists();
  const { addRecipeToGroceryList, loading: addToListLoading } = useAddRecipeToGroceryList();
  const { createList, loading: createListLoading } = useCreateList();
  const { updateList } = useUpdateList();
  const [servings, setServings] = useState<number | null>(null);
  const [showAddToListDialog, setShowAddToListDialog] = useState(false);
  const [selectedListId, setSelectedListId] = useState<string>('');
  const [selectedIngredients, setSelectedIngredients] = useState<Set<number>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [listSelectionError, setListSelectionError] = useState<string | null>(null);
  const [showCreateListInput, setShowCreateListInput] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalTitle, setAlertModalTitle] = useState('');
  const [alertModalMessage, setAlertModalMessage] = useState('');
  const [toast, setToast] = useState<{
    listName: string;
    addedCount: number;
    mergedCount: number;
    listId: string;
    previousItems: GroceryItem[];
  } | null>(null);

  const showAlert = useCallback((title: string, message: string) => {
    setAlertModalTitle(title);
    setAlertModalMessage(message);
    setShowAlertModal(true);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  if (loading) {
    return (
      <View style={[styles.container, styles.centered]}>
        <LinearGradient
          colors={[...Brand.primaryGradient]}
          style={styles.background}
        />
        <ActivityIndicator size="large" color={Brand.primary} />
      </View>
    );
  }

  if (error || !recipe) {
    return (
      <View style={[styles.container, styles.centered]}>
        <LinearGradient
          colors={[...Brand.primaryGradient]}
          style={styles.background}
        />
        <Text style={styles.errorText}>{error ? 'Failed to load recipe' : 'Recipe not found'}</Text>
        <Button onPress={() => router.back()}>Back to recipes</Button>
      </View>
    );
  }

  const currentServings = servings || recipe.servings;
  const servingMultiplier = currentServings / recipe.servings;

  const handleAddToList = () => {
    setSelectedIngredients(new Set(recipe.ingredients.map((_, i) => i)));
    const validId =
      groceryLists.length === 1
        ? groceryLists[0].id
        : (() => {
            const defaultOrFirst = settings.defaultGroceryListId || groceryLists[0]?.id || '';
            return groceryLists.some((l) => l.id === defaultOrFirst) ? defaultOrFirst : '';
          })();
    setSelectedListId(validId);
    setListSelectionError(null);
    setShowCreateListInput(false);
    setNewListName('');
    setShowAddToListDialog(true);
  };

  const handleConfirmAddToList = async () => {
    const listExists = groceryLists.some((l) => l.id === selectedListId);
    if (!selectedListId || !listExists) {
      setListSelectionError('Please select a list');
      return;
    }
    setListSelectionError(null);

    try {
      const indices = Array.from(selectedIngredients).sort((a, b) => a - b);
      const scaledStrings = indices.map((i) =>
        scaleIngredient(recipe.ingredients[i], servingMultiplier)
      );
      const result = await addRecipeToGroceryList(
        selectedListId,
        recipe,
        indices,
        scaledStrings
      );
      if (!result) {
        showAlert('Error', 'Failed to add ingredients. Please try again.');
        return;
      }
      trackEvent('Recipe added to list', { recipe_id: recipe.id, list_id: selectedListId });
      setShowAddToListDialog(false);
      const list = groceryLists.find((l) => l.id === selectedListId);
      refetchLists().catch(() => {});
      hapticSuccess();
      setToast({
        listName: list?.name ?? 'list',
        addedCount: result.addedCount,
        mergedCount: result.mergedCount,
        listId: selectedListId,
        previousItems: result.previousItems,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add ingredients. Please try again.';
      showAlert('Error', message);
    }
  };

  const handleCreateList = async () => {
    const validation = validateNewListName(newListName);
    if (!validation.valid) {
      setListSelectionError(validation.error);
      return;
    }
    setListSelectionError(null);
    try {
      const result = await createList(validation.trimmed);
      if (result) {
        trackEvent('Grocery list created', { list_id: result.list.id });
        await refetchLists(true, result.newHouseholdId);
        setSelectedListId(result.list.id);
        setNewListName('');
        setShowCreateListInput(false);
      } else {
        setListSelectionError('Failed to create list. Please try again.');
      }
    } catch {
      setListSelectionError('Failed to create list. Please try again.');
    }
  };

  const handleUndoAddToList = async () => {
    if (!toast) return;
    try {
      await updateList(toast.listId, { items: toast.previousItems });
      refetchLists().catch(() => {});
    } catch {
      showAlert('Error', 'Could not undo.');
    } finally {
      setToast(null);
    }
  };

  const toggleIngredient = (index: number) => {
    const newSelected = new Set(selectedIngredients);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedIngredients(newSelected);
  };

  const handleDelete = async () => {
    try {
      await deleteRecipe(recipe.id);
      setShowDeleteDialog(false);
      router.back();
    } catch {
      showAlert('Error', 'Failed to delete recipe. Please try again.');
    }
  };

  const imageSource = getRecipeImageSource(recipe.images ?? []);

  return (
    <View style={styles.container} testID="recipe-detail-screen">
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <ScrollView
        testID="recipe-detail-scroll"
        style={styles.scrollView}
        contentContainerStyle={[styles.content, { paddingTop: 16 + insets.top }]}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={20} color={Brand.primary} />
            <Text style={styles.backText}>Back</Text>
          </TouchableOpacity>
          <View style={styles.headerActions}>
            <Button variant="outline" size="sm" onPress={() => router.push({ pathname: '/(tabs)/recipes/edit', params: { id: recipe.id } })}>
              <Ionicons name="create-outline" size={16} color={Brand.primary} />
            </Button>
            <Button variant="outline" size="sm" onPress={() => setShowDeleteDialog(true)} style={styles.deleteButton}>
              <Ionicons name="trash-outline" size={16} color={Brand.destructive} />
            </Button>
          </View>
        </View>

        {/* Hero Image - only when recipe has an image */}
        {imageSource ? (
          <View style={styles.imageContainer}>
            <Image
              source={imageSource}
              style={styles.heroImage}
              resizeMode="cover"
            />
            <LinearGradient
              colors={['transparent', 'rgba(0,0,0,0.6)']}
              style={styles.imageGradient}
            />
            <View style={styles.imageTitle}>
              <Text style={styles.imageTitleText}>{recipe.title}</Text>
            </View>
          </View>
        ) : null}

        {/* Title and Info */}
        <View style={styles.infoSection}>
          <Text style={styles.title}>{recipe.title}</Text>
          {recipe.description && (
            <Text style={styles.description}>{recipe.description}</Text>
          )}
          
          <View style={styles.tagsContainer}>
            {recipe.tags.map(tag => (
              <Badge key={tag} variant="secondary" style={styles.tag}>
                {tag}
              </Badge>
            ))}
          </View>

          {/* Stats Cards */}
          <View style={styles.statsRow}>
            <View style={styles.statCard}>
              <Ionicons name="time-outline" size={20} color={Brand.primary} />
              <Text style={styles.statLabel}>Prep</Text>
              <Text style={styles.statValue}>{recipe.prepTime}<Text style={styles.statUnit}>min</Text></Text>
            </View>
            <View style={styles.statCard}>
              <Ionicons name="time-outline" size={20} color={Brand.primary} />
              <Text style={styles.statLabel}>Cook</Text>
              <Text style={styles.statValue}>{recipe.cookTime}<Text style={styles.statUnit}>min</Text></Text>
            </View>
            <View style={styles.statCard}>
              <Ionicons name="people-outline" size={20} color={Brand.primary} />
              <Text style={styles.statLabel}>Servings</Text>
              <View style={styles.servingsControl}>
                <TouchableOpacity
                  onPress={() => setServings(Math.max(1, currentServings - 1))}
                  style={styles.servingsButton}
                >
                  <Ionicons name="remove" size={16} color={Brand.primary} />
                </TouchableOpacity>
                <Text style={styles.servingsValue}>{currentServings}</Text>
                <TouchableOpacity
                  onPress={() => setServings(currentServings + 1)}
                  style={styles.servingsButton}
                >
                  <Ionicons name="add" size={16} color={Brand.primary} />
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>

        {/* Add to List Button */}
        <TouchableOpacity
          testID="add-to-list-btn"
          onPress={handleAddToList}
          style={styles.addToListButton}
          activeOpacity={0.8}
        >
          <LinearGradient
            colors={[...Brand.primaryGradientStrong]}
            style={styles.addToListGradient}
          >
            <Ionicons name="cart-outline" size={24} color="#fff" />
            <Text style={styles.addToListText}>Add to Grocery List</Text>
          </LinearGradient>
        </TouchableOpacity>

        {/* Ingredients and Instructions */}
        <View style={styles.detailsRow}>
          <Card style={styles.detailCard}>
            <CardContent>
              <View style={styles.sectionHeader}>
                <View style={styles.sectionIcon}>
                  <Text style={styles.sectionEmoji}>🥘</Text>
                </View>
                <Text style={styles.sectionTitle}>Ingredients</Text>
              </View>
              {recipe.ingredients.map((ingredient, index) => (
                <View key={index} style={styles.ingredientItem}>
                  <View style={styles.ingredientCheck}>
                    <Text style={styles.ingredientCheckText}>✓</Text>
                  </View>
                  <Text style={styles.ingredientText}>
                    {scaleIngredient(ingredient, servingMultiplier)}
                  </Text>
                </View>
              ))}
            </CardContent>
          </Card>

          <Card style={styles.detailCard}>
            <CardContent>
              <View style={styles.sectionHeader}>
                <View style={styles.sectionIcon}>
                  <Text style={styles.sectionEmoji}>👨‍🍳</Text>
                </View>
                <Text style={styles.sectionTitle}>Instructions</Text>
              </View>
              {recipe.instructions.map((instruction, index) => (
                <View key={index} style={styles.instructionItem}>
                  <View style={styles.instructionNumber}>
                    <Text style={styles.instructionNumberText}>{index + 1}</Text>
                  </View>
                  <Text style={styles.instructionText}>{instruction}</Text>
                </View>
              ))}
            </CardContent>
          </Card>
        </View>
      </ScrollView>

      {/* Add to List Dialog */}
      <Modal
        visible={showAddToListDialog}
        transparent
        animationType="none"
        onRequestClose={() => setShowAddToListDialog(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent} testID="ingredient-checklist-modal">
            <ScrollView
              testID="ingredient-checklist-scroll"
              style={styles.modalScrollBody}
              contentContainerStyle={styles.modalScrollContent}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={true}
            >
              <Text style={styles.modalTitle}>Add ingredients to grocery list</Text>
              <Text style={styles.modalDescription}>Select which ingredients you need and choose a list</Text>

              <View style={styles.modalSection}>
                <Text style={styles.modalLabel}>Select list</Text>
                <ScrollView style={styles.listSelector} nestedScrollEnabled>
                  {groceryLists.map(list => (
                    <TouchableOpacity
                      key={list.id}
                      onPress={() => {
                        setSelectedListId((prev) => (prev === list.id ? '' : list.id));
                        setListSelectionError(null);
                      }}
                      style={[
                        styles.listOption,
                        selectedListId === list.id && styles.listOptionSelected,
                      ]}
                    >
                      <Text style={[
                        styles.listOptionText,
                        selectedListId === list.id && styles.listOptionTextSelected,
                      ]}>
                        {list.name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                  {!showCreateListInput ? (
                    <TouchableOpacity
                      testID="add-to-list-create-new-list"
                      onPress={() => setShowCreateListInput(true)}
                      style={styles.createListOption}
                    >
                      <Ionicons name="add-circle-outline" size={20} color={Brand.primary} />
                      <Text style={styles.createListOptionText}>Create new list</Text>
                    </TouchableOpacity>
                  ) : (
                    <View style={styles.createListForm}>
                      <Input
                        testID="add-to-list-new-list-name"
                        placeholder="List name"
                        value={newListName}
                        onChangeText={(text) => {
                          setNewListName(text);
                          setListSelectionError(null);
                        }}
                        autoFocus
                      />
                      <View style={styles.createListFormActions}>
                        <Button
                          variant="outline"
                          size="sm"
                          onPress={() => {
                            setShowCreateListInput(false);
                            setNewListName('');
                            setListSelectionError(null);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          testID="add-to-list-create-list-btn"
                          size="sm"
                          onPress={handleCreateList}
                          disabled={createListLoading || !newListName.trim()}
                        >
                          {createListLoading ? 'Creating…' : 'Create'}
                        </Button>
                      </View>
                    </View>
                  )}
                </ScrollView>
              </View>

              <View style={styles.modalSection}>
                <Text style={styles.modalLabel}>
                  Ingredients {currentServings !== recipe.servings ? `(for ${currentServings} servings)` : ''}
                </Text>
                <ScrollView style={styles.ingredientsList} nestedScrollEnabled>
                  {recipe.ingredients.map((ingredient, index) => (
                    <TouchableOpacity
                      key={index}
                      onPress={() => toggleIngredient(index)}
                      style={styles.ingredientOption}
                    >
                      <Checkbox
                        testID={`ingredient-checkbox-${index}`}
                        checked={selectedIngredients.has(index)}
                        onCheckedChange={() => toggleIngredient(index)}
                      />
                      <Text style={styles.ingredientOptionText}>
                        {scaleIngredient(ingredient, servingMultiplier)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </ScrollView>

            <View style={styles.modalFooter}>
              {listSelectionError ? (
                <Text style={[styles.modalErrorText, styles.modalErrorTextAboveActions]}>{listSelectionError}</Text>
              ) : null}
              <View style={styles.modalActions}>
                <Button variant="outline" onPress={() => setShowAddToListDialog(false)}>
                  Cancel
                </Button>
                <Button testID="confirm-add-items-btn" onPress={handleConfirmAddToList} disabled={addToListLoading}>
                  {addToListLoading ? 'Adding…' : `Add ${selectedIngredients.size} items`}
                </Button>
              </View>
            </View>
          </View>
        </View>
      </Modal>

      {/* Success toast with Undo */}
      <Modal
        visible={!!toast}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => setToast(null)}
      >
        <View style={[styles.toastModalOverlay, { paddingBottom: insets.bottom + 56 }]}>
          <Pressable style={styles.toastDismissArea} onPress={() => setToast(null)} />
          <View style={styles.toastContainer} pointerEvents="box-none">
            {toast && (
              <View style={styles.toast} testID="success-toast">
                <Ionicons name="checkmark-circle" size={22} color={Brand.primary} />
                <Text style={styles.toastText} numberOfLines={2}>
                  Added to {toast.listName}
                  {toast.mergedCount > 0
                    ? ` (${toast.addedCount} new, ${toast.mergedCount} merged)`
                    : ` (${toast.addedCount} items)`}
                </Text>
                <TouchableOpacity onPress={handleUndoAddToList} style={styles.toastUndo}>
                  <Text style={styles.toastUndoText}>Undo</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={showDeleteDialog}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDeleteDialog(false)}
      >
        <View style={styles.confirmModalOverlay}>
          <View style={styles.confirmModalContent}>
            <Text style={styles.confirmModalTitle}>Delete recipe</Text>
            <Text style={styles.confirmModalDescription}>
              Are you sure you want to delete &quot;{recipe.title}&quot;? This action cannot be undone.
            </Text>
            <View style={styles.confirmModalActions}>
              <Button variant="outline" onPress={() => setShowDeleteDialog(false)} style={styles.confirmModalButton}>
                Cancel
              </Button>
              <Button variant="destructive" onPress={handleDelete} style={styles.confirmModalButton}>
                Delete
              </Button>
            </View>
          </View>
        </View>
      </Modal>

      {/* Alert modal (errors, limit reached) */}
      <Modal
        visible={showAlertModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAlertModal(false)}
      >
        <View style={styles.confirmModalOverlay}>
          <View style={styles.confirmModalContent}>
            <Text style={styles.confirmModalTitle}>{alertModalTitle}</Text>
            <Text style={styles.confirmModalDescription}>{alertModalMessage}</Text>
            <View style={styles.confirmModalActions}>
              <Button onPress={() => setShowAlertModal(false)} style={styles.confirmModalButton}>
                OK
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

function scaleIngredient(ingredient: string, multiplier: number): string {
  if (multiplier === 1) return ingredient;

  const match = ingredient.match(/^(\d+(?:\.\d+)?)\s*(.+)$/);
  if (match) {
    const amount = parseFloat(match[1]) * multiplier;
    const rest = match[2];
    const roundedAmount = Math.round(amount * 100) / 100;
    return `${roundedAmount} ${rest}`;
  }

  return ingredient;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 16,
    paddingBottom: 100,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    paddingVertical: 4,
  },
  backText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 8,
  },
  deleteButton: {
    borderColor: Neutrals.destructiveBorder,
  },
  imageContainer: {
    width: '100%',
    height: 250,
    borderRadius: 24,
    overflow: 'hidden',
    marginBottom: 24,
  },
  heroImage: {
    width: '100%',
    height: '100%',
  },
  heroImagePlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: Neutrals.cardBgSolid,
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '50%',
  },
  imageTitle: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 24,
  },
  imageTitleText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#fff',
  },
  infoSection: {
    marginBottom: 24,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 12,
  },
  description: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 16,
    lineHeight: 24,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 20,
  },
  tag: {
    marginRight: 4,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  statCard: {
    flex: 1,
    backgroundColor: Neutrals.whiteFaded70,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: Neutrals.whiteFaded70,
    padding: 16,
    alignItems: 'center',
  },
  statLabel: {
    fontSize: 12,
    color: Neutrals.textSecondary,
    marginTop: 8,
    marginBottom: 4,
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  statUnit: {
    fontSize: 14,
    fontWeight: 'normal',
    color: Neutrals.textSecondary,
  },
  servingsControl: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 8,
  },
  servingsButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: Brand.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  servingsValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    minWidth: 30,
    textAlign: 'center',
  },
  addToListButton: {
    marginBottom: 24,
    borderRadius: 16,
    overflow: 'hidden',
  },
  addToListGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 20,
  },
  addToListText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#fff',
  },
  detailsRow: {
    gap: 16,
  },
  detailCard: {
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },
  sectionIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: Brand.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionEmoji: {
    fontSize: 20,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  ingredientItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: Neutrals.whiteFaded70,
    borderRadius: 12,
    marginBottom: 8,
  },
  ingredientCheck: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: Brand.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ingredientCheckText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: Brand.primary,
  },
  ingredientText: {
    flex: 1,
    fontSize: 16,
    color: Neutrals.textPrimary,
  },
  instructionItem: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  instructionNumber: {
    width: 32,
    height: 32,
    borderRadius: 12,
    backgroundColor: Brand.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  instructionNumberText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#fff',
  },
  instructionText: {
    flex: 1,
    fontSize: 16,
    color: Neutrals.textPrimary,
    lineHeight: 24,
    paddingTop: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: Neutrals.overlay,
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    height: '80%',
  },
  modalScrollBody: {
    flex: 1,
  },
  modalScrollContent: {
    paddingBottom: 16,
  },
  modalFooter: {
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: Neutrals.border,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  modalDescription: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 24,
  },
  modalSection: {
    marginBottom: 24,
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    marginBottom: 12,
  },
  listSelector: {
    maxHeight: 120,
  },
  listOption: {
    padding: 16,
    borderRadius: 12,
    backgroundColor: Neutrals.surface,
    marginBottom: 8,
  },
  listOptionSelected: {
    backgroundColor: Brand.primary,
  },
  listOptionText: {
    fontSize: 16,
    color: Neutrals.textPrimary,
  },
  listOptionTextSelected: {
    color: '#fff',
    fontWeight: '600',
  },
  createListOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 16,
    borderRadius: 12,
    backgroundColor: Brand.primaryLighter,
    marginTop: 4,
  },
  createListOptionText: {
    fontSize: 16,
    color: Brand.primary,
    fontWeight: '600',
  },
  createListForm: {
    marginTop: 8,
  },
  createListFormActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  modalErrorText: {
    fontSize: 14,
    color: Brand.destructive,
  },
  modalErrorTextAboveActions: {
    marginBottom: 12,
  },
  ingredientsList: {
    maxHeight: 200,
  },
  ingredientOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    backgroundColor: Neutrals.surfaceLight,
  },
  ingredientOptionText: {
    flex: 1,
    fontSize: 14,
    color: Neutrals.textPrimary,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  confirmModalOverlay: {
    flex: 1,
    backgroundColor: Neutrals.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  confirmModalContent: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 10,
  },
  confirmModalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  confirmModalDescription: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  confirmModalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  confirmModalButton: {
    flex: 1,
  },
  errorText: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    textAlign: 'center',
    marginBottom: 16,
  },
  toastModalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'transparent',
    paddingHorizontal: 16,
  },
  toastDismissArea: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  toastContainer: {
    alignItems: 'center',
    zIndex: 1,
  },
  toast: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 16,
    paddingVertical: 14,
    paddingHorizontal: 16,
    gap: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 6,
    maxWidth: '100%',
  },
  toastText: {
    flex: 1,
    fontSize: 14,
    color: Neutrals.textPrimary,
  },
  toastUndo: {
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  toastUndoText: {
    fontSize: 15,
    fontWeight: '600',
    color: Brand.primary,
  },
});
